import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, Megaphone, Eye, EyeOff, Loader2, ImageIcon } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/lib/auth";

export default function Notices() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ caption: "", unit_id: "", image: null });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [n, u] = await Promise.all([api.get("/notices"), user?.role === "admin" ? api.get("/units") : Promise.resolve({ data: [] })]);
      setItems(n.data); setUnits(u.data);
    } catch (e) { toast.error("Erro ao carregar avisos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Envie apenas arquivos de imagem");
    if (f.size > 4 * 1024 * 1024) return toast.error("Imagem muito grande (máx 4MB)");
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, image: r.result }));
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.image) return toast.error("Escolha uma imagem primeiro");
    setUploading(true);
    try {
      await api.post("/notices", {
        image: form.image,
        caption: form.caption,
        unit_id: form.unit_id || null,
        active: true,
      });
      toast.success("Aviso publicado no portal do aluno!");
      setForm({ caption: "", unit_id: "", image: null });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao publicar"); }
    finally { setUploading(false); }
  };

  const toggle = async (n) => {
    try {
      await api.patch(`/notices/${n.id}`, { active: !n.active });
      toast.success(!n.active ? "Aviso reativado" : "Aviso ocultado do portal");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este aviso? Não poderá ser desfeito.")) return;
    try {
      await api.delete(`/notices/${id}`);
      toast.success("Aviso excluído");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Avisos" subtitle="Publique imagens que aparecem na aba Avisos do portal do aluno · Aluno rola pra baixo pra ver todos"/>

      {/* Upload card */}
      <Card className="p-5 border-slate-200" data-testid="notice-upload-card">
        <div className="flex items-center gap-2 mb-4">
          <Megaphone className="w-5 h-5 text-rose-600"/>
          <h3 className="font-heading text-base font-bold text-slate-900">Novo aviso</h3>
        </div>
        <div className="grid md:grid-cols-[240px,1fr] gap-5">
          <div>
            <Label>Imagem do aviso</Label>
            <div className="mt-1 aspect-square rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
              {form.image ? (
                <img src={form.image} alt="Preview" className="w-full h-full object-cover" data-testid="notice-preview"/>
              ) : (
                <div className="text-center text-slate-400 p-4">
                  <ImageIcon className="w-10 h-10 mx-auto mb-2 text-slate-300"/>
                  <div className="text-xs">Nenhuma imagem</div>
                </div>
              )}
            </div>
            <label className="mt-2 inline-flex items-center justify-center gap-2 cursor-pointer w-full bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-bold uppercase hover:bg-black" data-testid="notice-upload-input">
              <Upload className="w-4 h-4"/>Escolher imagem
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile}/>
            </label>
            {form.image && (
              <Button variant="outline" size="sm" onClick={() => { setForm((p)=>({...p, image: null})); if (fileRef.current) fileRef.current.value = ""; }} className="mt-2 w-full" data-testid="notice-clear">
                <Trash2 className="w-4 h-4 mr-2"/>Remover
              </Button>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <Label>Legenda / descrição (opcional)</Label>
              <Input value={form.caption} onChange={(e)=>setForm({ ...form, caption: e.target.value })} placeholder="Ex: Feriado — funcionamento das 8h às 12h" data-testid="notice-caption"/>
            </div>
            {user?.role === "admin" && units.length > 0 && (
              <div>
                <Label>Unidade (opcional — deixe em branco para todas)</Label>
                <Select value={form.unit_id || "__all__"} onValueChange={(v)=>setForm({ ...form, unit_id: v === "__all__" ? "" : v })}>
                  <SelectTrigger data-testid="notice-unit"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as unidades</SelectItem>
                    {units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="pt-2">
              <Button onClick={submit} disabled={uploading || !form.image} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase w-full md:w-auto" data-testid="notice-publish">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Publicando...</> : <><Megaphone className="w-4 h-4 mr-2"/>Publicar aviso</>}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* List */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-slate-600"/>
          <h3 className="font-heading font-bold text-slate-900">Avisos publicados</h3>
          <span className="text-xs text-slate-500">({items.length})</span>
        </div>
        {loading ? (
          <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin"/></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-2"/>
            Nenhum aviso publicado ainda — envie o primeiro acima.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-5" data-testid="notice-list">
            {items.map((n) => (
              <div key={n.id} className={`rounded-xl border overflow-hidden ${n.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}`} data-testid={`notice-${n.id}`}>
                <img src={n.image} alt="Aviso" className="w-full aspect-video object-cover"/>
                <div className="p-3">
                  {n.caption && <div className="text-sm text-slate-700 line-clamp-2 mb-2">{n.caption}</div>}
                  <div className="flex items-center justify-between text-[10px] text-slate-400 uppercase tracking-wider font-bold">
                    <span>{new Date(n.created_at).toLocaleDateString("pt-BR")} · {n.created_by_name || "—"}</span>
                    {!n.active && <span className="text-amber-600">Oculto</span>}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => toggle(n)} className="flex-1" data-testid={`notice-toggle-${n.id}`}>
                      {n.active ? <><EyeOff className="w-3 h-3 mr-1"/>Ocultar</> : <><Eye className="w-3 h-3 mr-1"/>Reativar</>}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(n.id)} className="text-rose-500 hover:bg-rose-50" data-testid={`notice-delete-${n.id}`}>
                      <Trash2 className="w-3 h-3"/>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
